import React, { useState, useEffect } from "react";
import "../css/Common.css";
import "./css/GroupsOrgsMgmt.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { useUser } from "../context/UserContext";
import { useRoleCheck } from "../../../hooks/useRoleCheck";
import APIService from "../services/APIService";
import UserGroupAPI from "../services/UserGroupAPI";
import UserAPI from "../services/UserAPI";

// Import our new components
import AccountOverview from "./components/AccountOverview";
import OrganizationSection from "./components/OrganizationSection";
import CreateGroupModal from "./components/CreateGroupModal";
import UserAssignmentModal from "./components/UserAssignmentModal";
import LoadingSpinner from "../common/LoadingSpinner";

function GroupsOrgsMgmt() {
  const { loggedInUser } = useUser();
  const { canAccessAccountManagement, isLoading: roleLoading } = useRoleCheck();

  // State management
  const [accountStructure, setAccountStructure] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedOrganizations, setExpandedOrganizations] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [showAllGroups, setShowAllGroups] = useState(false);

  // Group management state
  const [showCreateGroupForm, setShowCreateGroupForm] = useState(false);
  const [createGroupData, setCreateGroupData] = useState({
    name: "",
    description: "",
    accountId: null,
    orgId: null,
  });
  const [createGroupLoading, setCreateGroupLoading] = useState(false);
  const [createGroupError, setCreateGroupError] = useState("");

  // User assignment state
  const [showUserAssignment, setShowUserAssignment] = useState(false);
  const [selectedUserForAssignment, setSelectedUserForAssignment] =
    useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentError, setAssignmentError] = useState("");

  // Success states
  const [successMessage, setSuccessMessage] = useState("");

  console.log("GroupsOrgsMgmt component rendering...");
  console.log("GroupsOrgsMgmt: loggedInUser:", loggedInUser);

  // Fetch account structure when component mounts or loggedInUser changes
  useEffect(() => {
    if (loggedInUser && loggedInUser.uuid) {
      fetchAccountStructureForLoggedInUser();
    } else {
      console.log("GroupsOrgsMgmt: No logged-in user available");
    }
  }, [loggedInUser]);

  const fetchAccountStructureForLoggedInUser = async () => {
    console.log(
      "GroupsOrgsMgmt: Fetching account structure for UUID:",
      loggedInUser.uuid,
    );
    setLoading(true);
    setError(null);

    try {
      const response = await APIService.get(
        `/api/v1/base/accounts/structure/by-user`,
      );
      console.log("GroupsOrgsMgmt: Account structure response:", response);
      setAccountStructure(response);
    } catch (err) {
      console.error("GroupsOrgsMgmt: Error fetching account structure:", err);
      setError("Failed to load account structure. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Group creation handlers
  const handleCreateGroupClick = () => {
    setCreateGroupData({
      name: "",
      description: "",
      accountId: accountStructure?.account?.accountId || null,
      orgId: null,
    });
    setCreateGroupError("");
    setShowCreateGroupForm(true);
  };

  const handleCreateGroupSubmit = async (e) => {
    e.preventDefault();
    setCreateGroupLoading(true);
    setCreateGroupError("");

    try {
      const groupData = {
        ...createGroupData,
        accountId: accountStructure.account.accountId,
      };

      const response = await UserGroupAPI.createGroup(groupData);

      // SECURITY: Check for groupName instead of groupId (groupId is no longer returned)
      if (response.success || response.groupName) {
        setSuccessMessage("Group created successfully!");
        setShowCreateGroupForm(false);
        // Refresh the account structure
        await fetchAccountStructureForLoggedInUser();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setCreateGroupError("Failed to create group. Please try again.");
      }
    } catch (error) {
      console.error("Error creating group:", error);
      setCreateGroupError(
        error.message || "Failed to create group. Please try again.",
      );
    } finally {
      setCreateGroupLoading(false);
    }
  };

  const handleCreateGroupCancel = () => {
    setShowCreateGroupForm(false);
    setCreateGroupData({
      name: "",
      description: "",
      accountId: null,
      orgId: null,
    });
    setCreateGroupError("");
  };

  // Group update handler
  // SECURITY: Updated to use groupName instead of groupId for API calls
  const handleGroupUpdate = async (groupName, updateData) => {
    try {
      console.log("Updating group with name:", groupName);
      console.log("Update data:", updateData);
      console.log("Account structure:", accountStructure);

      // Ensure we have the accountId (needed for internal processing, not exposed)
      const accountId = accountStructure?.account?.accountId;
      if (!accountId) {
        throw new Error(
          "Account ID is not available. Please refresh the page and try again.",
        );
      }

      // Use groupName as identifier - don't include groupId in update data
      const completeUpdateData = {
        ...updateData,
        groupName: groupName,
      };

      console.log("Complete update data:", completeUpdateData);

      // SECURITY: Use updateGroupByName instead of updateGroup(groupId, data)
      const response = await UserGroupAPI.updateGroupByName(
        groupName,
        completeUpdateData,
      );

      // SECURITY: Check for groupName instead of groupId (groupId is no longer returned)
      if (response.success || response.groupName) {
        setSuccessMessage("Group updated successfully!");
        // Refresh the account structure
        await fetchAccountStructureForLoggedInUser();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        throw new Error("Failed to update group");
      }
    } catch (error) {
      console.error("Error updating group:", error);
      throw error; // Re-throw to let the component handle the error
    }
  };

  // User assignment handlers
  const handleUserAssignmentClick = async (user) => {
    setSelectedUserForAssignment(user);
    setAssignmentError("");
    setAssignmentLoading(true);

    try {
      const response = await UserAPI.getAvailableGroupsForUser(user.uuid);
      setAvailableGroups(response.groups || []);
      setShowUserAssignment(true);
    } catch (error) {
      console.error("Error fetching available groups:", error);
      setAssignmentError("Failed to load available groups. Please try again.");
    } finally {
      setAssignmentLoading(false);
    }
  };

  // SECURITY: Updated to use groupName instead of groupId
  const handleUserGroupAssignment = async (groupName) => {
    if (!selectedUserForAssignment) return;

    setAssignmentLoading(true);
    setAssignmentError("");

    try {
      // SECURITY: Use updateUserGroupByName instead of updateUserGroup
      const response = await UserAPI.updateUserGroupByName(
        selectedUserForAssignment.uuid,
        groupName,
      );

      if (response.success) {
        setSuccessMessage(
          `User ${selectedUserForAssignment.firstName} ${selectedUserForAssignment.lastName} has been assigned to the selected group.`,
        );
        setShowUserAssignment(false);
        setSelectedUserForAssignment(null);
        // Refresh the account structure
        await fetchAccountStructureForLoggedInUser();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setAssignmentError("Failed to assign user to group. Please try again.");
      }
    } catch (error) {
      console.error("Error assigning user to group:", error);
      setAssignmentError(
        error.message || "Failed to assign user to group. Please try again.",
      );
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleUserAssignmentCancel = () => {
    setShowUserAssignment(false);
    setSelectedUserForAssignment(null);
    setAvailableGroups([]);
    setAssignmentError("");
  };

  const toggleOrganization = (orgId) => {
    const newExpanded = new Set(expandedOrganizations);
    if (newExpanded.has(orgId)) {
      newExpanded.delete(orgId);
    } else {
      newExpanded.add(orgId);
    }
    setExpandedOrganizations(newExpanded);
  };

  // SECURITY: Updated to use groupName as identifier instead of groupId
  const toggleGroup = (groupName) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  // Helper function to collect all users from all groups
  // SECURITY: Uses user.uuid as unique identifier instead of userId
  const getAllUsers = () => {
    if (!accountStructure) return [];

    const allUsers = [];
    const userMap = new Map(); // To avoid duplicates

    // Add users from organizations
    if (accountStructure.organizations) {
      accountStructure.organizations.forEach((org) => {
        org.groups.forEach((group) => {
          if (group.users) {
            group.users.forEach((user) => {
              if (!userMap.has(user.uuid)) {
                userMap.set(user.uuid, {
                  ...user,
                  groupName: group.name || group.groupName,
                  orgName: org.name,
                });
              }
            });
          }
        });
      });
    }

    // Add users from direct groups
    if (accountStructure.directGroups) {
      accountStructure.directGroups.forEach((group) => {
        if (group.users) {
          group.users.forEach((user) => {
            if (!userMap.has(user.uuid)) {
              userMap.set(user.uuid, {
                ...user,
                groupName: group.name || group.groupName,
                orgName: "Direct Account Group",
              });
            }
          });
        }
      });
    }

    return Array.from(userMap.values());
  };

  // Helper function to collect all groups
  const getAllGroups = () => {
    if (!accountStructure) return [];

    const allGroups = [];

    // Add groups from organizations
    if (accountStructure.organizations) {
      accountStructure.organizations.forEach((org) => {
        org.groups.forEach((group) => {
          allGroups.push({ ...group, orgName: org.name, isDirectGroup: false });
        });
      });
    }

    // Add direct groups
    if (accountStructure.directGroups) {
      accountStructure.directGroups.forEach((group) => {
        allGroups.push({
          ...group,
          orgName: "Direct Account Group",
          isDirectGroup: true,
        });
      });
    }

    return allGroups;
  };

  if (!loggedInUser) {
    return (
      <div className="common-container">
        <div className="account-error">
          <h2 className="main-title">Groups & Organizations</h2>
          <div className="error-message">
            <p>You need to be logged in to view groups and organizations.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while checking roles
  if (roleLoading) {
    return (
      <div className="common-container">
        <LoadingSpinner message="Checking permissions..." />
      </div>
    );
  }

  // Check if user has permission to access Account Management
  if (!canAccessAccountManagement()) {
    return (
      <div className="common-container">
        <div className="account-error">
          <h2 className="main-title">Access Denied</h2>
          <div className="error-message">
            <p>
              You don't have permission to access Account Management. Only
              account administrators can view this page.
            </p>
            <p>Required roles: Account Admin or Platform Admin</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="common-container">
        <LoadingSpinner message="Loading Groups & Organizations..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="common-container">
        <div className="account-error">
          <h2 className="main-title">Groups & Organizations</h2>
          <div className="error-message">
            <p>{error}</p>
            <button
              onClick={() => fetchAccountStructureForLoggedInUser()}
              className="common-button"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!accountStructure) {
    return (
      <div className="common-container">
        <div className="account-error">
          <h2 className="main-title">Groups & Organizations</h2>
          <div className="error-message">
            <p>No groups or organizations were found for your account.</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="common-container">
      <div className="common-header">
        <h1 className="main-title">Groups & Organizations</h1>
        <p className="common-subtitle">
          Manage your account structure and member relationships
        </p>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="common-message success">
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>{successMessage}</span>
          </div>
        )}
      </div>

      <div className="common-layout">
        <div className="common-content">
          {/* Account Overview */}
          <AccountOverview
            accountStructure={accountStructure}
            showAllUsers={showAllUsers}
            showAllGroups={showAllGroups}
            onToggleAllUsers={() => setShowAllUsers(!showAllUsers)}
            onToggleAllGroups={() => setShowAllGroups(!showAllGroups)}
            onCreateGroupClick={handleCreateGroupClick}
            onUserAssignmentClick={handleUserAssignmentClick}
            onGroupUpdate={handleGroupUpdate}
            createGroupLoading={createGroupLoading}
            getAllUsers={getAllUsers}
            getAllGroups={getAllGroups}
          />

          {/* Organizations */}
          {accountStructure.organizations &&
            accountStructure.organizations.length > 0 && (
              <div className="common-section">
                <div className="common-section-header">
                  <h3>
                    <FontAwesomeIcon icon={faBuilding} />
                    Organizations
                  </h3>
                  <p>View organizations and their associated groups</p>
                </div>
                <div className="common-section-content">
                  {accountStructure.organizations.map((organization) => (
                    <OrganizationSection
                      key={organization.orgId}
                      organization={organization}
                      isExpanded={expandedOrganizations.has(organization.orgId)}
                      onToggle={toggleOrganization}
                      expandedGroups={expandedGroups}
                      onGroupToggle={toggleGroup}
                      onUserAssignmentClick={handleUserAssignmentClick}
                    />
                  ))}
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Create Group Modal */}
      <CreateGroupModal
        show={showCreateGroupForm}
        createGroupData={createGroupData}
        createGroupLoading={createGroupLoading}
        createGroupError={createGroupError}
        onSubmit={handleCreateGroupSubmit}
        onCancel={handleCreateGroupCancel}
        onChange={setCreateGroupData}
      />

      {/* User Assignment Modal */}
      <UserAssignmentModal
        show={showUserAssignment}
        selectedUser={selectedUserForAssignment}
        availableGroups={availableGroups}
        assignmentLoading={assignmentLoading}
        assignmentError={assignmentError}
        onGroupAssignment={handleUserGroupAssignment}
        onCancel={handleUserAssignmentCancel}
      />
    </div>
  );
}

export default GroupsOrgsMgmt;
